import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CheckDto } from 'src/app/shared/models/check-dto';
import { CheckSources } from 'src/app/shared/models/check-sources.enum';
import { Customer } from 'src/app/shared/models/Customer/Customer.entity';
import { CustomerSubscriptionItem } from 'src/app/shared/models/CustomerSubscription.item';
import { CustomerTokenItem } from 'src/app/shared/models/CustomerToken.item';
import { CustomerAddressDto, CustomerDto, CustomerVisitDto } from 'src/app/shared/models/DTO/customer-dto';
import { MyFocusLoyaltyMemberDTO } from 'src/app/shared/models/DTO/MyFocus-Loyalty.dto';
import { AppMessageSeverity } from 'src/app/shared/models/enums/AppMessageSeverity.enum';
import { CreditCardType } from 'src/app/shared/models/enums/CreditCardType.enum';
import { CustomerSubscriptionFrequency } from 'src/app/shared/models/enums/customer-subscription-frequency.enum';
import { AppService } from './app.service';
import { FocuslinkService } from './focuslink.service';
import { OOVenueService } from './venue-service';

@Injectable({
	providedIn: 'root',
})
export class CustomerService {
	customerList: BehaviorSubject<CustomerDto[]> = new BehaviorSubject<CustomerDto[]>([]);
	customerListUrl: {
		url: string;
		lastModified: string;
	};
	customer: BehaviorSubject<CustomerDto> = new BehaviorSubject<CustomerDto>(new CustomerDto());
	customerKey: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
	customerSummary: {
		recordCount: number;
		topCustomer: CustomerDto;
	};

	currentVenueKey: number;
	customerSubscriptionItemList: BehaviorSubject<CustomerSubscriptionItem[]> = new BehaviorSubject<CustomerSubscriptionItem[]>([]);

	initatiatedVenueKey: number;

	publicCustomer: BehaviorSubject<Customer> = new BehaviorSubject<Customer>(new Customer());

	venueSubscription: Subscription = new Subscription();

	constructor(private app: AppService, private focuslink: FocuslinkService, public venueService: OOVenueService, private httpService: HttpClient) {
		this.venueSubscription.add(
			this.venueService.subscriptionVenue.subscribe((venue) => {
				if (venue && venue.key) {
					this.customerList.next([]);
					this.customerListUrl = undefined;
				}
			})
		);
	}

	async addressSave(address: CustomerAddressDto): Promise<CustomerAddressDto> {
		const resp = await this.focuslink.customerAddressPost(this.customer.value.key, address);
		return address;
	}

	async customerLookup(customerLookupQuery: CustomerLookupQuery): Promise<Customer[]> {
		return await this.focuslink.customerLookup(this.currentVenueKey, customerLookupQuery);
	}

	async initPublicCustomer(customerKey) {
		this.customerKey.next(customerKey);
		const resp = await this.focuslink.customerGetOneCustomerRecord(this.customerKey.value);
		this.publicCustomer.next(new Customer(resp));
		return this.customer.value;
	}

	async initCustomerService(prefetch: boolean = false) {
		this.currentVenueKey = this.venueService.subscriptionVenue.value.key;
		this.customerList.next([]);

		this.customerListPrefetch(this.venueService.subscriptionVenue.value.key);
		if (prefetch) {
			return;
		}
		this.customerListGetFromS3();
		this.initatiatedVenueKey = this.currentVenueKey;
	}

	async customerListPrefetch(venueKey: number) {
		const resp = await this.focuslink.customerGetAll(venueKey, false);
		this.customerListUrl = {
			url: resp.redirectUrl,
			lastModified: resp.lastModified,
		};
		this.customerSummary = resp.summary;
		return this.customerListUrl;
	}

	async customerListGetFromS3() {
		try {
			const resp: any = await this.httpService.get(this.customerListUrl.url).toPromise();

			const tempCustomerList = resp.records.map((i) => {
				const customer = new CustomerDto(i);
				customer.averageSpend = customer.totalRevenue / customer.visitCount;
				customer.loyaltyMember = customer.loyaltyId ? true : false;
				return customer;
			});

			tempCustomerList.sort((a, b) => (a.visitCount < b.visitCount ? 1 : -1));
			this.customerList.next(tempCustomerList);
			return this.customerList.value;
		} catch (error) {
			console.log(error);
		}
	}

	async setCustomer(customerKey) {
		this.customerKey.next(customerKey);
		const resp = await this.focuslink.customerGetOne(this.venueService.venueKey, customerKey);
		const tempCustomer = new CustomerDto(resp);
		tempCustomer.addresses.forEach((address) => {
			address.displayName = `${this.venueService.app.references.toTitleCase(address.street1)} ${this.venueService.app.references.toTitleCase(
				address.city
			)}, ${address.state} ${address.postalCode}`;
		});

		this.customer.next(new CustomerDto(resp));
		this.subscriptions();
		this.visitsGet();
		this.tokens();
		this.loyaltyGet();
	}

	async loyaltyGet() {
		if (this.customer.value.loyaltyId || this.customer.value.phone[0]) {
			try {
				const resp = await this.focuslink.loyaltyMemberInquiry(
					this.venueService.venueKey,
					this.customer.value.loyaltyId || this.customer.value.phone[0],
					this.customer.value.loyaltyId ? 'account' : 'phone'
				);
				this.customer.value.loyalty = new MyFocusLoyaltyMemberDTO(resp);
				this.customer.next(this.customer.value);
			} catch (error) {}
		}
	}

	async subscriptions() {
		const resp = await this.focuslink.customerSubscriptionGetOne(this.venueService.venueKey, this.customerKey.value);
		this.customer.value.subscriptions = resp.map((i) => new CustomerSubscriptionItem(i));
		this.customer.value.subscriptions?.forEach((subscription) => {
			subscription.ooMenuItem = this.venueService.ooMenuItems.find((item) => item.menuItemKey === subscription.menuItemKey);
		});
		this.customer.next(this.customer.value);
	}

	async subscriptionsGetAll() {
		const resp = await this.focuslink.customerSubscriptionGetAll(this.venueService.venueKey);
		const subscriptionItems: CustomerSubscriptionItem[] = resp.map((i) => new CustomerSubscriptionItem(i));

		subscriptionItems.forEach((item) => {
			item.nextBillDateDate = this.venueService.app.moment.unix(item.nextBillDate).toDate();
			item.ooMenuItem = this.venueService.ooMenuItems.find((ooItem) => ooItem.menuItemKey === item.menuItemKey);
			item.customerDto = this.customerList.value.find((customer) => customer.key === item.customerKey);
			item.frequencyDisplayName = this.venueService.app.references.toTitleCase(CustomerSubscriptionFrequency[item.frequency]);
		});

		this.customerSubscriptionItemList.next(subscriptionItems);
		return this.customerSubscriptionItemList.value;
	}

	async tokens() {
		const resp = await this.focuslink.customerTokenGetAll(this.venueService.venueKey, this.customer.value.key);
		this.customer.value.tokens = resp.map((i) => new CustomerTokenItem(i));
		this.customer.value.tokens.forEach((token) => {
			token.displayName = token.type ? `${CreditCardType[token.type]} ${token.cardNumber}` : token.cardNumber;
			token.displayName += ` last used: ${moment(token.lastUseDateTime).format('l')}`;
		});
		this.customer.next(this.customer.value);
		return this.customer.value;
	}

	async visitsGet() {
		const resp = await this.focuslink.customerVisitsGet(this.venueService.venueKey, this.customerKey.value);
		const visits = resp.map((i) => new CustomerVisitDto(i));

		this.customer.value.visits = visits
			.filter((i) => i.source === CheckSources.POS || i.source === CheckSources.None)
			.map((i) => new CheckDto(i.check))
			.sort((a, b) => (moment(a.created).unix() < moment(b.created).unix() ? 1 : -1));
		this.customer.value.onlineOrders = visits
			.filter((i) => i.source !== CheckSources.POS && i.source !== CheckSources.None)
			.map((i) => new CheckDto(i.check))
			.sort((a, b) => (moment(a.created).unix() < moment(b.created).unix() ? 1 : -1));

		this.customer.next(this.customer.value);
	}

	async save() {
		await this.focuslink.customerPatch(
			this.venueService.venueKey,
			this.customer.value.key,
			this.customer.value.toCustomerVenue(this.venueService.venueKey)
		);
		this.customer.next(this.customer.value);
	}

	async subscriptionSave(subscriptionItem: CustomerSubscriptionItem) {
		let resp;

		if (subscriptionItem.sk) {
			resp = await this.focuslink.customerSubscriptionPatch(
				this.venueService.venueKey || +subscriptionItem.pk,
				this.customer.value.key,
				subscriptionItem.sk,
				subscriptionItem
			);
		} else {
			resp = await this.focuslink.customerSubscriptionPost(this.venueService.venueKey || +subscriptionItem.pk, this.customer.value.key, subscriptionItem);
		}

		const newItem = new CustomerSubscriptionItem(resp);
		newItem.ooMenuItem = this.venueService.ooMenuItems.find((i) => i.menuItemKey === newItem.menuItemKey);

		if (this.publicCustomer?.value.customerKey) {
			let idx = this.publicCustomer.value.subscriptions.findIndex((i) => i.sk === newItem.sk);
			if (idx === -1) {
				this.publicCustomer.value.subscriptions.push(newItem);
			} else {
				if (!newItem.inactive) {
					this.publicCustomer.value.subscriptions[idx] = newItem;
				}
			}

			this.publicCustomer.next(this.publicCustomer.value);
			return this.publicCustomer.value;
		}

		if (!this.customer.value.subscriptions) this.customer.value.subscriptions = [];

		let idx = this.customer.value.subscriptions.findIndex((i) => i.sk === newItem.sk);
		if (idx === -1) {
			this.customer.value.subscriptions.push(newItem);
		} else {
			this.customer.value.subscriptions[idx] = newItem;
		}

		this.customer.next(this.customer.value);
		this.venueService.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: '', detail: `Subscription saved successfully.` });
		return this.customer.value;
	}

	async subscriptionDelete(subscriptionKey: string) {
		await this.focuslink.customerSubscriptionDelete(this.venueService.venueKey, this.customer.value.key, subscriptionKey);
		this.customer.value.subscriptions.splice(
			this.customer.value.subscriptions.findIndex((s) => s.sk === subscriptionKey),
			1
		);
		this.customer.next(this.customer.value);
		return this.customer.value;
	}
}

export interface CustomerLookupQuery {
	firstname: string;
	lastname: string;
	email: string;
	phone: string;
}
